import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Trash2, Archive, RefreshCw, Loader2, AlertTriangle, 
  Database, Users, Calendar, Shield, Eye, RotateCcw,
  CheckCircle, XCircle, Info, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DataManagement = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [archives, setArchives] = useState([]);
  
  // Filter states
  const [filterType, setFilterType] = useState('test_data');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [namePattern, setNamePattern] = useState('');
  
  // Preview states
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [archiveInstead, setArchiveInstead] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    fetchStats();
    fetchArchives();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/data-management/clinic/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load data statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchives = async () => {
    try {
      const response = await axios.get(`${API}/data-management/clinic/archives`);
      setArchives(response.data);
    } catch (error) {
      console.error('Failed to fetch archives:', error);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    
    try {
      const response = await axios.post(`${API}/data-management/clinic/preview-delete`, {
        filter_type: filterType,
        start_date: dateRange.start || null,
        end_date: dateRange.end || null,
        name_pattern: namePattern || null,
        password: 'preview', // Dummy for preview
        confirm_deletion: false
      });
      setPreview(response.data);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    if (!password) {
      toast.error('Password is required');
      return;
    }
    
    setDeleting(true);
    
    try {
      const response = await axios.post(`${API}/data-management/clinic/bulk-delete`, {
        filter_type: filterType,
        start_date: dateRange.start || null,
        end_date: dateRange.end || null,
        name_pattern: namePattern || null,
        password: password,
        confirm_deletion: true,
        archive_instead: archiveInstead
      });
      
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setPreview(null);
      setPassword('');
      setConfirmText('');
      fetchStats();
      fetchArchives();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Delete operation failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreArchive = async (archiveId) => {
    const pwd = prompt('Enter your password to restore this archive:');
    if (!pwd) return;
    
    try {
      const response = await axios.post(
        `${API}/data-management/clinic/restore-archive/${archiveId}`,
        null,
        { params: { password: pwd } }
      );
      toast.success(response.data.message);
      fetchStats();
      fetchArchives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore archive');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-management">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Database className="h-6 w-6 text-teal-600" />
          Data Management
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage and clean up your clinic data securely
        </p>
      </div>

      {/* Security Notice */}
      <Alert className="border-blue-300 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <strong>Security Notice:</strong> All data operations require password verification and are logged for audit purposes. 
          Data is archived by default and can be restored within 90 days.
        </AlertDescription>
      </Alert>

      {/* Statistics Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Users className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.test_members || 0}</p>
                <p className="text-sm text-gray-500">Test Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Users className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.inactive_members || 0}</p>
                <p className="text-sm text-gray-500">Inactive Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Database className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.old_audit_logs || 0}</p>
                <p className="text-sm text-gray-500">Old Logs (90d+)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.orphaned_records || 0}</p>
                <p className="text-sm text-gray-500">Orphaned Records</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="cleanup" className="space-y-4">
        <TabsList>
          <TabsTrigger value="cleanup" className="gap-2">
            <Trash2 className="h-4 w-4" />
            Bulk Cleanup
          </TabsTrigger>
          <TabsTrigger value="archives" className="gap-2">
            <Archive className="h-4 w-4" />
            Archives ({archives.length})
          </TabsTrigger>
        </TabsList>

        {/* Bulk Cleanup Tab */}
        <TabsContent value="cleanup">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Data Cleanup</CardTitle>
              <CardDescription>
                Select criteria to identify and remove test or inactive data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filter Selection */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label>Cleanup Type</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test_data">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-amber-50">Recommended</Badge>
                          Test Data (test/demo/sample emails)
                        </div>
                      </SelectItem>
                      <SelectItem value="inactive">Inactive Members</SelectItem>
                      <SelectItem value="date_range">By Date Range</SelectItem>
                      <SelectItem value="name_pattern">By Name/Email Pattern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {filterType === 'date_range' && (
                  <div className="space-y-3">
                    <Label>Date Range (Created Date)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        placeholder="Start date"
                      />
                      <Input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        placeholder="End date"
                      />
                    </div>
                  </div>
                )}

                {filterType === 'name_pattern' && (
                  <div className="space-y-3">
                    <Label>Name/Email Pattern</Label>
                    <Input
                      value={namePattern}
                      onChange={(e) => setNamePattern(e.target.value)}
                      placeholder="e.g., test, demo, @example.com"
                    />
                    <p className="text-xs text-gray-500">
                      Searches in both name and email fields (case-insensitive)
                    </p>
                  </div>
                )}
              </div>

              {/* Preview Button */}
              <div className="flex gap-3">
                <Button onClick={handlePreview} disabled={previewing} variant="outline">
                  {previewing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-2" />
                  )}
                  Preview Data
                </Button>
                <Button onClick={fetchStats} variant="ghost" size="icon">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>

              {/* Preview Results */}
              {preview && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-lg">
                        {preview.total_count} records will be affected
                      </h4>
                      <p className="text-sm text-gray-500">
                        Including: {preview.related_data.appointments} appointments, {preview.related_data.payments} payments, {preview.related_data.medical_notes} notes
                      </p>
                    </div>
                    <Button 
                      variant="destructive" 
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={preview.total_count === 0}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Proceed to Delete
                    </Button>
                  </div>

                  {/* Warnings */}
                  {preview.warnings.length > 0 && (
                    <Alert className="border-amber-300 bg-amber-50">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription>
                        {preview.warnings.map((w, i) => (
                          <p key={i} className="text-amber-800">{w}</p>
                        ))}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Preview Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Patient ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.items.slice(0, 10).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.full_name}</TableCell>
                            <TableCell>{item.email}</TableCell>
                            <TableCell className="font-mono text-sm">{item.patient_id}</TableCell>
                            <TableCell>
                              <Badge variant={item.membership_status === 'active' ? 'default' : 'secondary'}>
                                {item.membership_status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {preview.items.length > 10 && (
                      <div className="p-3 bg-gray-50 text-center text-sm text-gray-500">
                        And {preview.total_count - 10} more records...
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Archives Tab */}
        <TabsContent value="archives">
          <Card>
            <CardHeader>
              <CardTitle>Data Archives</CardTitle>
              <CardDescription>
                Previously archived data that can be restored
              </CardDescription>
            </CardHeader>
            <CardContent>
              {archives.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No archives found</p>
                  <p className="text-sm">Archives are created when you use the "Archive instead of delete" option</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {archives.map((archive, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">
                          {archive.member_count} members archived
                        </p>
                        <p className="text-sm text-gray-500">
                          {archive.reason} • {new Date(archive.archived_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-400">
                          Includes {archive.appointment_count || 0} appointments
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRestoreArchive(archive._id || archive.id)}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Data Deletion
            </DialogTitle>
            <DialogDescription>
              This action will {archiveInstead ? 'archive' : 'permanently delete'} {preview?.total_count || 0} members and all their related data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Archive Option */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-gray-500" />
                <span className="text-sm">Archive instead of permanent delete</span>
              </div>
              <Switch 
                checked={archiveInstead} 
                onCheckedChange={setArchiveInstead}
              />
            </div>
            
            {!archiveInstead && (
              <Alert className="border-red-300 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Warning:</strong> Permanent deletion cannot be undone!
                </AlertDescription>
              </Alert>
            )}

            {/* Password Field */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Enter your password to confirm
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your admin password"
              />
            </div>

            {/* Confirmation Text */}
            <div className="space-y-2">
              <Label>Type DELETE to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="DELETE"
                className="font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting || confirmText !== 'DELETE' || !password}
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {archiveInstead ? 'Archive Data' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagement;
